import React, { PureComponent, Fragment } from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { TextField } from '@src/components/TextField';
import { Button } from '@src/components/Button';
import Checkbox from '@src/components/Checkbox';
import { Tooltip } from '@src/components/Tooltip';
import { QuestionIcon } from '@src/components/QuestionIcon';
import { closeModal } from '@src/redux/actions/modal';
import api from '@src/util/api';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { trackEvent } from '@src/metrics';
import './styles.scss';

const STEPS = {
  FAST_TRACK: 'fast_track',
};

@connect(null, {
  closeModal,
})
export default class FastTrackModal extends PureComponent {
  static propTypes = {
    closeModal: PropTypes.func.isRequired,
    companyId: PropTypes.string,
    email: PropTypes.string.isRequired,
  };

  state = {
    currentStep: STEPS.FAST_TRACK,
    isExternalUrl: false,
    link: '',
    salary: '',
    salaryAssistance: false,
    sendForReview: true,
    sending: false,
    sent: false,
  };

  sendFastTrackLink = async evt => {
    evt.preventDefault();
    const { link, isExternalUrl, salaryAssistance, salary, sendForReview } = this.state;
    const { companyId } = this.props;
    this.setState({ sending: true });

    await api.post('/api/fast-track', {
      companyId,
      isExternalUrl,
      link,
      salary,
      salaryAssistance,
      sendForReview,
    });

    this.setState({ sending: false, sent: true });
    trackEvent({
      context: MetricContext.SENDING_FAST_TRACK,
      interaction: MetricAction.SEND_FAST_TRACK_LINK,
    });
    this.setNextStep();
  };

  setNextStep = () => {
    const { currentStep } = this.state;

    setTimeout(this.props.closeModal, currentStep === STEPS.FAST_TRACK ? 3000 : 0);
  };

  handleLinkChange = value => this.setState({ link: value });

  handleSalaryChange = value => this.setState({ salary: value });

  handleIsExternalUrlChange = () => {
    this.setState(
      ({ isExternalUrl }) => ({ isExternalUrl: !isExternalUrl }),
      () => {
        const { isExternalUrl } = this.state;
        trackEvent({
          context: MetricContext.SENDING_FAST_TRACK,
          interaction: MetricAction.SET_FAST_TRACK_IS_EXTERNAL_APPLICATION,
          payload: { 'Is External APPLICATION': isExternalUrl },
        });
      },
    );
  };

  handleSalaryAssistanceChange = () => {
    this.setState(
      ({ salaryAssistance }) => ({ salaryAssistance: !salaryAssistance }),
      () => {
        const { salaryAssistance } = this.state;
        trackEvent({
          context: MetricContext.SENDING_FAST_TRACK,
          interaction: MetricAction.SET_ASSISTANCE_WITH_SALARY_BUDGET,
          payload: { 'Need Assistance': salaryAssistance },
        });
      },
    );
  };

  handleSendForReviewChange = () => {
    this.setState(({ sendForReview }) => ({ sendForReview: !sendForReview }));
  };

  render() {
    const { email } = this.props;
    const { link, salary, sending, sent, currentStep } = this.state;
    const disableButton = !link || sending || sent;

    switch (currentStep) {
      case STEPS.FAST_TRACK: {
        return (
          <Fragment>
            <div className="fast-track-modal-header">Fast track link</div>

            <div className="fast-track-modal-info">
              We can set up your open position on MeetFrank based on your existing details.
              <br />
              Submit a link with the details below or just e-mail them to us via
              <br />
              <a href={`mailto:${email}`}>{email}</a>
            </div>

            <form onSubmit={this.sendFastTrackLink}>
              <TextField
                defaultValue={link}
                inputClassName="position-details-link"
                label="Add a link to your position details"
                onBlur={e =>
                  trackEvent({
                    context: MetricContext.SENDING_FAST_TRACK,
                    interaction: MetricAction.FILL_FAST_TRACK_LINK,
                    payload: { Link: e.target.value },
                  })
                }
                onChange={this.handleLinkChange}
                wrapperClassName="position-details-link-wrapper"
              />

              <br />

              <div className="fast-track-modal-checkbox-wrapper">
                <Checkbox
                  label="Candidates apply through this link"
                  onChange={this.handleIsExternalUrlChange}
                  value={this.state.isExternalUrl}
                  wrapperClassName="fast-track-modal-input-checkbox"
                />
              </div>

              <br />
              <hr />

              <div className="fast-track-modal-info">
                <strong>Specify the gross salary range for the given position or positions</strong>
              </div>

              <div className="fast-track-modal-input">
                <TextField
                  defaultValue={salary}
                  label="Salary range (e.g. 1500 - 3000 EUR / month)"
                  onBlur={e =>
                    trackEvent({
                      context: MetricContext.SENDING_FAST_TRACK,
                      interaction: MetricAction.SET_FAST_TRACK_SALARY_RANGE,
                      payload: { 'Salary Range': e.target.value },
                    })
                  }
                  onChange={this.handleSalaryChange}
                  wrapperClassName="fast-track-modal-input-wrapper"
                />

                <br />

                <div className="fast-track-modal-checkbox-wrapper">
                  <Checkbox
                    label="Please assist us with the salary budget"
                    onChange={this.handleSalaryAssistanceChange}
                    value={this.state.salaryAssistance}
                    wrapperClassName="fast-track-modal-input-checkbox"
                  />

                  <Tooltip
                    overlay={
                      <span>
                        Your account manager will review the <br />
                        position and will send you a suggestion <br />
                        based on the available talent pool.
                      </span>
                    }
                    placement="top"
                  >
                    <span className="fast-track-modal-input-checkbox-info-wrapper">
                      <QuestionIcon />
                    </span>
                  </Tooltip>
                </div>
              </div>

              <hr />
              {/* <br />

              <div className='fast-track-modal-checkbox-wrapper'>
                <Checkbox
                  label='I want to review the opening before publishing'
                  onChange={ this.handleSendForReviewChange }
                  value={ this.state.sendForReview }
                  wrapperClassName='fast-track-modal-input-checkbox'
                />
              </div> */}

              <br />

              {sent && (
                <div className="fast-track-modal-confirmation">
                  Thank you! We've received your fast-track request. Your account manager will check <br />
                  and prepare it as soon as possible.
                </div>
              )}

              <Button
                className={classnames('fast-track-modal-send', sent && 'light')}
                isDisabled={disableButton}
                type="submit"
              >
                {sending ? 'Sending...' : sent ? 'Link sent' : 'Send'}
              </Button>
            </form>
          </Fragment>
        );
      }
    }
  }
}
